import parse, { domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import {
  FREE_ARTICLE_CHUNK_SIZE,
  FREE_ARTICLE_END_CONTENT_BUFFER,
  FREE_ARTICLE_MIN_LENGTH_FOR_CTAS,
  FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS,
} from '@/constants/constants';
import type { DOMNode } from 'html-react-parser';
import type { ReactNode } from 'react';

/**
 * Formats article content based on whether it's a paid or free article
 */
export const formatArticleContent = (
  content: string | undefined,
  isPaid: boolean,
  isAuthenticated: boolean,
  renderCta: () => ReactNode
) => {
  if (content === undefined) return;

  return isPaid || isAuthenticated ? formatPaidArticleContent(content) : formatFreeArticleContent(content, renderCta);
};

/**
 * Formats paid article content without adding CTAs
 */
export const formatPaidArticleContent = (content: string) => {
  const options = createBaseParserOptions();
  return parse(content, options);
};

/**
 * Formats free article content with CTAs inserted at appropriate intervals
 */
export const formatFreeArticleContent = (content: string, renderCta: () => ReactNode) => {
  const imageCount = (content.match(/<img/g) || []).length;
  const textLength = content.replace(/<[^>]*>/g, '').length;
  const totalContentLength = textLength + imageCount * FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS;
  const isImageHeavy = imageCount > 0 && textLength < 500;

  const options = createParserOptionsWithCtas(content, isImageHeavy, imageCount, totalContentLength, renderCta);
  return parse(content, options);
};

/**
 * Creates parser options with CTA insertion logic based on content type
 */
export const createParserOptionsWithCtas = (
  content: string,
  isImageHeavy: boolean,
  totalImages: number,
  totalContentLength: number,
  renderCta: () => ReactNode
) => {
  const baseOptions = createBaseParserOptions();

  if (isImageHeavy) {
    return createImageHeavyParserOptions(totalImages, renderCta);
  }

  const plainText = content.replace(/<[^>]*>/g, '');
  const effectiveContentLength = plainText.length + totalImages * FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS;

  if (effectiveContentLength < FREE_ARTICLE_MIN_LENGTH_FOR_CTAS) {
    return baseOptions;
  }

  return createTextHeavyParserOptions(totalContentLength, renderCta);
};

/**
 * Creates base parser options for handling h1 tags and links
 */
export const createBaseParserOptions = () => {
  const options: HTMLReactParserOptions = {
    replace(domNode: DOMNode) {
      if (domNode instanceof Element && domNode.name === 'h1') {
        return <h2>{domToReact((domNode as Element).children as DOMNode[])}</h2>;
      }

      if (domNode instanceof Element && domNode.name === 'a') {
        const { attribs } = domNode;
        return (
          <a href={attribs.href} target='_blank' rel='noopener noreferrer'>
            {domToReact((domNode as Element).children as DOMNode[])}
          </a>
        );
      }
    },
  };
  return options;
};

/**
 * Creates parser options for image-heavy content
 */
export const createImageHeavyParserOptions = (totalImages: number, renderCta: () => ReactNode) => {
  let imgCounter = 0;

  const options: HTMLReactParserOptions = {
    ...createBaseParserOptions(),
    replace(domNode: DOMNode) {
      const baseResult = createBaseParserOptions().replace?.(domNode, 0);
      if (baseResult !== undefined) return baseResult;

      if (domNode instanceof Element && domNode.name === 'img') {
        imgCounter++;
        // Don't add CTA after the last or second-to-last image
        if (imgCounter % 2 === 0 && imgCounter < totalImages - 1) {
          return (
            <>
              <img {...(domNode as Element).attribs} />
              {renderCta()}
            </>
          );
        }
      }
    },
  };

  return options;
};

/**
 * Creates parser options for text-heavy content
 */
export const createTextHeavyParserOptions = (totalContentLength: number, renderCta: () => ReactNode) => {
  let characterCount = 0;
  let insertedCtaCount = 0;
  let lastNodeWasImage = false;
  let charactersSinceLastCta = 0;
  const maxCtas = Math.floor(totalContentLength / FREE_ARTICLE_CHUNK_SIZE);
  const idealCtaSpacing = Math.floor(totalContentLength / (maxCtas + 1));

  const containsNestedImage = (node: Element): boolean => {
    if (node.name === 'img') return true;

    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (child instanceof Element) {
          if (containsNestedImage(child)) return true;
        }
      }
    }
    return false;
  };

  const options: HTMLReactParserOptions = {
    ...createBaseParserOptions(),
    replace(domNode: DOMNode) {
      const baseResult = createBaseParserOptions().replace?.(domNode, 0);
      if (baseResult !== undefined) return baseResult;

      if (domNode instanceof Element) {
        if (domNode.name === 'img') {
          const imageCharacters = FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS;
          characterCount += imageCharacters;
          charactersSinceLastCta += imageCharacters;
          lastNodeWasImage = true;
          return domToReact([domNode] as DOMNode[]);
        }

        if (['figure', 'table', 'tbody', 'tr', 'td'].includes(domNode.name) && containsNestedImage(domNode)) {
          const imageCharacters = FREE_ARTICLE_IMAGE_EQUIVALENT_CHARACTERS;
          characterCount += imageCharacters;
          charactersSinceLastCta += imageCharacters;
          lastNodeWasImage = true;
          return domToReact([domNode] as DOMNode[]);
        }

        if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'span', 'a'].includes(domNode.name)) {
          const blockText = domToReact(domNode.children as DOMNode[]).toString();
          const textLength = blockText.length;
          characterCount += textLength;
          charactersSinceLastCta += textLength;

          if (lastNodeWasImage) {
            lastNodeWasImage = false;
            return domToReact([domNode] as DOMNode[]);
          }

          if (
            charactersSinceLastCta >= idealCtaSpacing &&
            insertedCtaCount < maxCtas &&
            domNode.name === 'p' &&
            totalContentLength - characterCount > FREE_ARTICLE_END_CONTENT_BUFFER
          ) {
            insertedCtaCount++;
            charactersSinceLastCta = 0;
            return (
              <>
                {domToReact([domNode] as DOMNode[])}
                {renderCta()}
              </>
            );
          }

          lastNodeWasImage = false;
        } else {
          lastNodeWasImage = false;
        }
      }
    },
  };

  return options;
};
